'use client'

import React, { useEffect, useRef, useState, useCallback } from 'react'
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { cn } from '@/utilities/ui'
import { SectionWrapper } from '@/components/SectionWrapper'
import SectionHeader from '@/components/ui/SectionHeader'
import LinkWithIcon from '@/components/ui/LinkWithIcon'
import { CarouselNavButton } from '@/components/ui/CarouselNavButton'
import { Media } from '@/components/Media'
import type { LatestProjectsBlock, Project } from '@/payload-types'

type LatestProjectCardData = Pick<
  Project,
  'id' | 'slug' | 'title' | 'place'
> & {
  featuredImage?: Project['featuredImage'] | null
  products?: Project['products'] | null
}

interface LatestProjectsClientProps {
  projects: LatestProjectCardData[]
  header: LatestProjectsBlock['header']
  showAllLink?: LatestProjectsBlock['showAllLink']
  allProjectsLinkText?: LatestProjectsBlock['allProjectsLinkText']
  allProjectsLinkUrl?: LatestProjectsBlock['allProjectsLinkUrl']
  settings?: LatestProjectsBlock['settings']
}

export default function LatestProjectsClient({
  projects,
  header,
  showAllLink = true,
  allProjectsLinkText = 'Alle Projekte anzeigen',
  allProjectsLinkUrl = '/projekte',
  settings,
}: LatestProjectsClientProps) {
  const normalizedHeader = {
    infoTagText: header?.infoTagText ?? undefined,
    title: header?.title || 'Neueste Projekte',
    subtitle: header?.subtitle ?? undefined,
  }

  return (
    <SectionWrapper settings={settings}>
      <SectionHeader
        infoTagText={normalizedHeader.infoTagText || ''}
        title={normalizedHeader.title}
        subtitle={normalizedHeader.subtitle}
        titleClassName="uppercase"
        subtitleClassName="text-text-secondary"
      />

      <Carousel projects={projects} />

      {showAllLink && (
        <div className="mt-8">
          <LinkWithIcon
            href={allProjectsLinkUrl || '/projekte'}
            text={allProjectsLinkText || 'Alle Projekte anzeigen'}
            variant="link"
            color="brand"
            size="md"
          />
        </div>
      )}
    </SectionWrapper>
  )
}

// --- Carousel ---

function Carousel({ projects }: { projects: LatestProjectCardData[] }) {
  const carouselRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)

  const checkScrollability = useCallback(() => {
    if (!carouselRef.current) return
    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)
  }, [])

  useEffect(() => {
    checkScrollability()
    const el = carouselRef.current
    if (!el) return
    el.addEventListener('scroll', checkScrollability)
    window.addEventListener('resize', checkScrollability)
    return () => {
      el.removeEventListener('scroll', checkScrollability)
      window.removeEventListener('resize', checkScrollability)
    }
  }, [checkScrollability])

  const getCardScrollAmount = () => {
    if (!carouselRef.current) return 300
    const firstCard = carouselRef.current.querySelector('a')
    if (!firstCard) return 300
    return firstCard.offsetWidth + 16 // card width + gap-4
  }

  const scrollLeft = () => {
    if (!carouselRef.current) return
    carouselRef.current.scrollBy({ left: -getCardScrollAmount(), behavior: 'smooth' })
  }

  const scrollRight = () => {
    if (!carouselRef.current) return
    carouselRef.current.scrollBy({ left: getCardScrollAmount(), behavior: 'smooth' })
  }

  return (
    <div className="relative w-full">
      {/* Scroll buttons */}
      <div className="flex justify-end gap-2 mb-4">
        <CarouselNavButton
          direction="prev"
          onClick={scrollLeft}
          disabled={!canScrollLeft}
          aria-label="Zurück scrollen"
          className="relative z-40"
        />
        <CarouselNavButton
          direction="next"
          onClick={scrollRight}
          disabled={!canScrollRight}
          aria-label="Weiter scrollen"
          className="relative z-40"
        />
      </div>

      {/* Scrollable cards container */}
      <div
        ref={carouselRef}
        className="flex w-full overflow-x-scroll scroll-smooth py-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="flex gap-4 pl-0">
          {projects.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </div>
      </div>
    </div>
  )
}

// --- Card ---

function ProjectCard({
  project,
  index,
}: {
  project: LatestProjectCardData
  index: number
}) {
  const [isHovered, setIsHovered] = useState(false)

  // Get the first product name as category label
  const category = (() => {
    if (!project.products?.length) return null
    const first = project.products[0]
    if (typeof first === 'object' && first !== null) return first.title || null
    return null
  })()

  return (
    <motion.a
      href={`/projekte/${project.slug}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94],
        delay: Math.min(index * 0.06, 0.4),
      }}
      className="group/card relative z-10 flex w-[340px] flex-shrink-0 overflow-hidden cursor-pointer md:w-[400px] lg:w-[440px]"
      data-card-type="project"
      data-card-slug={project.slug || undefined}
      data-card-block="latestProjects"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Full card is the image */}
      <div className="relative aspect-[2/3] w-full overflow-hidden bg-neutral-100">
        {typeof project.featuredImage === 'object' && project.featuredImage ? (
          <motion.div
            className="absolute inset-0"
            animate={{ scale: isHovered ? 1.05 : 1 }}
            transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <Media
              resource={project.featuredImage}
              alt={project.title}
              fill
              pictureClassName="block h-full w-full"
              imgClassName="h-full w-full object-cover"
              size="(max-width: 768px) 90vw, (max-width: 1280px) 45vw, 440px"
            />
          </motion.div>
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-neutral-200">
            <span className="text-sm text-neutral-500">Kein Bild</span>
          </div>
        )}

        {/* Persistent gradient overlay for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        {/* Text content inside card, bottom-aligned */}
        <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
          {category && (
            <p className="font-ui text-xs uppercase tracking-wider text-white/70 mb-2">
              {category}
            </p>
          )}
          <h3 className="title-md uppercase text-white leading-tight">
            {project.title}
          </h3>
          {project.place && (
            <p className="body-sm text-white/70 mt-2">{project.place}</p>
          )}
        </div>
      </div>
    </motion.a>
  )
}
